// tabs/ThinkyBaaSLedger.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDatabase, faCheckCircle, faUpload, faPlay } from "@fortawesome/free-solid-svg-icons";

const dummyLedgers = [
  {
    id: 1,
    name: "Financial Transactions Ledger",
    description: "Records all invoice payments and receipts immutably.",
    initialized: false,
    updated: false,
    verified: false,
    log: [],
    precondition: true,
  },
  {
    id: 2,
    name: "Supply Chain Ledger",
    description: "Tracks shipment milestones across the blockchain network.",
    initialized: false,
    updated: false,
    verified: false,
    log: [],
    precondition: false,
  },
  {
    id: 3,
    name: "Student Records Ledger",
    description: "Stores course completion and blockchain-verified certificate data.",
    initialized: false,
    updated: false,
    verified: false,
    log: [],
    precondition: true,
  },
];

const ThinkyBaaSLedger = () => {
  const [ledgers, setLedgers] = useState(dummyLedgers.map(l => ({ ...l, log: l.log || [] })));

  // Initialize ledger
  const initializeLedger = (id) => {
    setLedgers(prev =>
      prev.map(l =>
        l.id === id && !l.initialized
          ? { ...l, initialized: true, log: [...(l.log || []), { event: "Ledger Initialized", time: new Date().toLocaleTimeString() }] }
          : l
      )
    );
  };

  // Update ledger
  const updateLedger = (id) => {
    setLedgers(prev =>
      prev.map(l => {
        if (l.id === id) {
          if (!l.precondition) {
            return { ...l, log: [...(l.log || []), { event: "Update blocked: precondition not met", time: new Date().toLocaleTimeString() }] };
          }
          return { ...l, updated: true, log: [...(l.log || []), { event: "Ledger Updated", time: new Date().toLocaleTimeString() }] };
        }
        return l;
      })
    );
  };

  // Verify ledger
  const verifyLedger = (id) => {
    setLedgers(prev =>
      prev.map(l =>
        l.id === id && l.updated && !l.verified
          ? { ...l, verified: true, log: [...(l.log || []), { event: "Verified on blockchain", time: new Date().toLocaleTimeString() }] }
          : l
      )
    );
  };

  return (
    <div className="bg-gray-900 p-6 rounded-2xl shadow-lg space-y-6">
      <div className="flex items-center mb-4">
        <FontAwesomeIcon icon={faDatabase} className="text-blue-400 text-xl lg:text-2xl mr-4" />
        <h2 className="text-lg lg:text-xl font-bold">Ledger & Data Workflow</h2>
      </div>

      <p className="text-gray-300 text-sm">
        This page simulates a blockchain <span className="font-semibold">ledger workflow</span>: Initialize → Update → Verify.
        Each ledger shows an event log and reflects real-time state changes.
      </p>

      <div className="space-y-4">
        {ledgers.map(l => (
          <div key={l.id} className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <h3 className="text-xl font-semibold mb-1">{l.name}</h3>
            <p className="text-gray-400 mb-2">{l.description}</p>

            <div className="flex flex-wrap gap-2 mb-2">
              <span className={`px-2 py-1 rounded-sm text-sm ${l.initialized ? "bg-green-600" : "bg-yellow-600"}`}>
                {l.initialized ? "Initialized ✅" : "Pending Initialization ⏳"}
              </span>
              <span className={`px-2 py-1 rounded-sm text-sm ${l.updated ? "bg-green-600" : "bg-yellow-600"}`}>
                {l.updated ? "Updated ✅" : "Pending Update ⏳"}
              </span>
              <span className={`px-2 py-1 rounded-sm text-sm ${l.verified ? "bg-green-600" : "bg-yellow-600"}`}>
                {l.verified ? "Verified ✅" : "Pending Verification ⏳"}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                disabled={l.initialized}
                onClick={() => initializeLedger(l.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  l.initialized ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faUpload} className="mr-1" /> Initialize
              </button>
              <button
                disabled={!l.initialized || l.updated}
                onClick={() => updateLedger(l.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  !l.initialized || l.updated ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faPlay} className="mr-1" /> Update
              </button>
              <button
                disabled={!l.updated || l.verified}
                onClick={() => verifyLedger(l.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  !l.updated || l.verified ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Verify
              </button>
            </div>

            <div className="mt-3 bg-gray-900 p-2 rounded-lg border border-gray-700">
              <h4 className="text-gray-300 font-semibold mb-1">Event Log:</h4>
              {(!l.log || l.log.length === 0) ? (
                <p className="text-gray-500 text-sm">No events yet.</p>
              ) : (
                <ul className="text-gray-400 text-sm list-disc list-inside space-y-1">
                  {l.log?.map((log, idx) => (
                    <li key={idx}>
                      [{log.time}] {log.event}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThinkyBaaSLedger;
